/**
 * O que faz: Gráficos da aba Follow-up — melhor horário e melhor dia da semana para enviar (barras
 *            verticais no degradê lima do Dashboard), respostas por mensagem da sequência e tempo até
 *            a resposta (barras horizontais, sem biblioteca).
 * Onde usa: FollowupCampaignMetrics na aba Follow-up.
 * Conexões: Recharts e contrato FollowupAnalytics (RPC followup_analytics). Paleta: seção 12 do design system.
 * Tabelas: nenhuma; recebe dados agregados da RPC.
 * Data: 02/09/2026 (redesenho no Gens Design System e gráfico por dia da semana em 07/10/2026).
 */

import { useId, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  MINIMO_ENVIOS_INSIGHT_HORA,
  type FollowupAnalytics,
  type FollowupAnalyticsFaixa,
} from '@/features/followup/analytics';

interface FollowupAnalyticsChartsProps {
  analytics: FollowupAnalytics;
}

const numero = new Intl.NumberFormat('pt-BR');
const percentual = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

const FAIXA_LABELS: Record<FollowupAnalyticsFaixa['faixa'], string> = {
  ate_15_min: 'Até 15 min',
  de_15_min_a_1h: '15 min a 1 h',
  de_1h_a_6h: '1 h a 6 h',
  de_6h_a_24h: '6 h a 24 h',
  acima_24h: 'Mais de 24 h',
};

const FAIXA_ORDER: FollowupAnalyticsFaixa['faixa'][] = [
  'ate_15_min',
  'de_15_min_a_1h',
  'de_1h_a_6h',
  'de_6h_a_24h',
  'acima_24h',
];

// O Postgres devolve o dia da semana como DOW (0 = domingo). A semana aparece de segunda a domingo.
const NOMES_DIA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const NOMES_DIA_COMPLETO = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const ORDEM_DIAS = [1, 2, 3, 4, 5, 6, 0];

// Cores da seção 12.2 (sequencial verde): enviadas em tom claro, respostas em tom escuro.
const COR_ENVIADAS = '#cfe29a';
const COR_RESPONDIDAS = '#5c8a1d';
const COR_FAIXA = '#8fb83a';

interface PontoTaxa {
  rotulo: string;
  rotuloCompleto: string;
  taxa: number;
  enviadas: number;
  respondidas: number;
}

/** Moldura comum dos gráficos: card branco, título Sora e, opcionalmente, um destaque à direita. */
const CartaoGrafico = ({ titulo, descricao, destaque, children }: { titulo: string; descricao: string; destaque?: ReactNode; children: ReactNode }) => (
  <section className="flex min-w-0 flex-col rounded-3xl border border-border bg-card p-5 shadow-[var(--lg-sombra-1)]">
    <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <h3 className="text-[15px] leading-snug text-foreground">{titulo}</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{descricao}</p>
      </div>
      {destaque}
    </div>
    {children}
  </section>
);

/** Selo do melhor resultado: só aparece quando o volume é suficiente para não enganar. */
const SeloMelhor = ({ texto }: { texto: string }) => (
  <span className="lg-selo bg-accent text-accent-foreground">{texto}</span>
);

const DicaTaxa = ({ active, payload, prefixo }: { active?: boolean; payload?: { payload: PontoTaxa }[]; prefixo: string }) => {
  if (!active || !payload?.length) return null;
  const ponto = payload[0].payload;
  return (
    <div className="rounded-2xl border border-border bg-card px-3.5 py-2.5 shadow-[var(--lg-sombra-2)]">
      <p className="text-xs font-medium text-muted-foreground">{prefixo} {ponto.rotuloCompleto}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{percentual.format(ponto.taxa)}% responderam</p>
      <p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
        {numero.format(ponto.respondidas)} de {numero.format(ponto.enviadas)} mensagens
      </p>
    </div>
  );
};

/**
 * Barras verticais de taxa de resposta no estilo do Dashboard: todas em lima vivo e a barra
 * sob o mouse "acende" (degradê mais claro + brilho), como o usuário aprovou em 05/10/2026.
 */
const BarrasTaxa = ({ dados, prefixoDica }: { dados: PontoTaxa[]; prefixoDica: string }) => {
  const idBase = useId().replace(/:/g, '');
  const [barraAtiva, setBarraAtiva] = useState<number | null>(null);
  const mostrarValores = dados.length <= 14;
  const eixo = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };

  return (
    <div className="h-[220px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dados}
          margin={{ top: 22, right: 4, left: -6, bottom: 0 }}
          barCategoryGap="22%"
          onMouseMove={(estado) => setBarraAtiva(typeof estado?.activeTooltipIndex === 'number' ? estado.activeTooltipIndex : null)}
          onMouseLeave={() => setBarraAtiva(null)}
        >
          <defs>
            <linearGradient id={`${idBase}-comum`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#d8ff3c" />
              <stop offset="55%" stopColor="#bada55" />
              <stop offset="100%" stopColor="#5c8a1d" />
            </linearGradient>
            <linearGradient id={`${idBase}-destaque`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ecff8a" />
              <stop offset="45%" stopColor="#d8ff3c" />
              <stop offset="100%" stopColor="#7fa53a" />
            </linearGradient>
            <filter id={`${idBase}-brilho`} x="-50%" y="-20%" width="200%" height="140%">
              <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#bada55" floodOpacity="0.55" />
            </filter>
          </defs>
          <CartesianGrid strokeDasharray="3 5" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="rotulo" tick={eixo} axisLine={false} tickLine={false} tickMargin={8} interval="preserveStartEnd" />
          <YAxis tick={eixo} axisLine={false} tickLine={false} unit="%" width={44} allowDecimals={false} />
          <Tooltip content={<DicaTaxa prefixo={prefixoDica} />} cursor={false} />
          <Bar dataKey="taxa" radius={[8, 8, 3, 3]} maxBarSize={44} isAnimationActive={false}>
            {dados.map((ponto, indice) => (
              <Cell
                key={ponto.rotulo}
                fill={indice === barraAtiva ? `url(#${idBase}-destaque)` : `url(#${idBase}-comum)`}
                filter={indice === barraAtiva ? `url(#${idBase}-brilho)` : undefined}
              />
            ))}
            <LabelList
              dataKey="taxa"
              content={({ x, y, width, value, index }: { x?: number | string; y?: number | string; width?: number | string; value?: number | string; index?: number }) => {
                const emDestaque = index === barraAtiva;
                if (value === undefined || (!mostrarValores && !emDestaque)) return null;
                return (
                  <text
                    x={Number(x) + Number(width) / 2}
                    y={Number(y) - 7}
                    textAnchor="middle"
                    style={{ fontSize: 11, fontWeight: emDestaque ? 700 : 600, fill: 'hsl(var(--foreground))', fontFamily: "'Sora', 'DM Sans', system-ui, sans-serif", fontVariantNumeric: 'tabular-nums' }}
                  >
                    {percentual.format(Number(value))}%
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Escolhe o melhor ponto com volume mínimo; com pouco envio a taxa engana e o selo não aparece. */
const melhorPonto = (pontos: PontoTaxa[]): PontoTaxa | null => {
  const comVolume = pontos.filter((ponto) => ponto.enviadas >= MINIMO_ENVIOS_INSIGHT_HORA);
  if (comVolume.length < 2) return null;
  return [...comVolume].sort((a, b) => b.taxa - a.taxa || b.enviadas - a.enviadas)[0];
};

export const FollowupAnalyticsCharts = ({ analytics }: FollowupAnalyticsChartsProps) => {
  const horarios: PontoTaxa[] = analytics.horarios
    .filter((item) => item.enviadas > 0)
    .map((item) => ({ rotulo: `${item.hora}h`, rotuloCompleto: `às ${item.hora}h`, taxa: item.taxa, enviadas: item.enviadas, respondidas: item.respondidas }));

  const dias: PontoTaxa[] = ORDEM_DIAS
    .map((dia) => analytics.dias.find((item) => item.dia === dia))
    .filter((item): item is FollowupAnalytics['dias'][number] => Boolean(item && item.enviadas > 0))
    .map((item) => ({ rotulo: NOMES_DIA[item.dia] ?? String(item.dia), rotuloCompleto: `na ${NOMES_DIA_COMPLETO[item.dia] ?? item.dia}`, taxa: item.taxa, enviadas: item.enviadas, respondidas: item.respondidas }));

  const etapas = analytics.etapas.filter((item) => item.enviadas > 0).sort((a, b) => a.sequencia - b.sequencia);
  const faixas = FAIXA_ORDER
    .map((faixa) => analytics.faixas_tempo.find((item) => item.faixa === faixa))
    .filter((item): item is FollowupAnalyticsFaixa => Boolean(item));
  const totalFaixas = faixas.reduce((soma, item) => soma + item.quantidade, 0);

  if (horarios.length === 0 && dias.length === 0 && etapas.length === 0 && totalFaixas === 0) {
    return null;
  }

  const melhorHora = melhorPonto(horarios);
  const melhorDia = melhorPonto(dias);
  const maiorEnvioEtapa = Math.max(...etapas.map((etapa) => etapa.enviadas), 1);
  const maiorFaixa = Math.max(...faixas.map((faixa) => faixa.quantidade), 1);
  // Respostas que chegaram na primeira hora: resume a faixa de tempo numa frase.
  const respostasPrimeiraHora = faixas
    .filter((faixa) => faixa.faixa === 'ate_15_min' || faixa.faixa === 'de_15_min_a_1h')
    .reduce((soma, faixa) => soma + faixa.quantidade, 0);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {horarios.length > 0 && (
        <CartaoGrafico
          titulo="Melhor horário para enviar"
          descricao="Porcentagem de mensagens respondidas por hora do envio (horário de Brasília)."
          destaque={melhorHora && <SeloMelhor texto={`Melhor: ${melhorHora.rotulo} · ${percentual.format(melhorHora.taxa)}%`} />}
        >
          <BarrasTaxa dados={horarios} prefixoDica="Envios" />
        </CartaoGrafico>
      )}

      {dias.length > 0 && (
        <CartaoGrafico
          titulo="Melhor dia da semana"
          descricao="Porcentagem de mensagens respondidas por dia do envio."
          destaque={melhorDia && <SeloMelhor texto={`Melhor: ${melhorDia.rotulo} · ${percentual.format(melhorDia.taxa)}%`} />}
        >
          <BarrasTaxa dados={dias} prefixoDica="Envios" />
        </CartaoGrafico>
      )}

      {etapas.length > 0 && (
        <CartaoGrafico
          titulo="Respostas por mensagem da sequência"
          descricao="Quantas vezes cada mensagem saiu e quantas foram respondidas."
        >
          {/* Legenda sempre que há 2 séries (seção 12.1); cor nunca é a única pista, o número vem escrito. */}
          <div className="mb-3 flex flex-wrap gap-4 text-xs text-muted-foreground" aria-hidden="true">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: COR_ENVIADAS }} />Enviadas</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: COR_RESPONDIDAS }} />Respondidas</span>
          </div>
          <ol className="space-y-3.5">
            {etapas.map((etapa) => (
              <li key={etapa.sequencia} className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-3 text-[13px]">
                  <span className="font-semibold text-foreground">Mensagem {etapa.sequencia}</span>
                  <span className="tabular-nums text-muted-foreground">
                    <strong className="font-semibold text-foreground">{numero.format(etapa.respondidas)}</strong> de {numero.format(etapa.enviadas)} · {percentual.format(etapa.taxa)}%
                  </span>
                </div>
                <div className="relative h-2.5 overflow-hidden rounded-full bg-secondary" role="img" aria-label={`Mensagem ${etapa.sequencia}: ${etapa.enviadas} enviadas, ${etapa.respondidas} respondidas`}>
                  <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(etapa.enviadas / maiorEnvioEtapa) * 100}%`, background: COR_ENVIADAS }} />
                  <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(etapa.respondidas / maiorEnvioEtapa) * 100}%`, background: COR_RESPONDIDAS }} />
                </div>
              </li>
            ))}
          </ol>
        </CartaoGrafico>
      )}

      {totalFaixas > 0 && (
        <CartaoGrafico
          titulo="Tempo até a resposta"
          descricao="Intervalo entre o envio e a primeira resposta do lead."
          destaque={respostasPrimeiraHora > 0 && (
            <SeloMelhor texto={`${Math.round((respostasPrimeiraHora / totalFaixas) * 100)}% na 1ª hora`} />
          )}
        >
          <ul className="space-y-3">
            {faixas.map((faixa) => {
              const fatia = totalFaixas > 0 ? (faixa.quantidade / totalFaixas) * 100 : 0;
              return (
                <li key={faixa.faixa} className="grid grid-cols-[88px_1fr_auto] items-center gap-3 text-[13px]">
                  <span className="text-muted-foreground">{FAIXA_LABELS[faixa.faixa]}</span>
                  <span className="h-2.5 overflow-hidden rounded-full bg-secondary" aria-hidden="true">
                    <span className="block h-full rounded-full" style={{ width: `${(faixa.quantidade / maiorFaixa) * 100}%`, background: COR_FAIXA }} />
                  </span>
                  <span className="min-w-[64px] text-right tabular-nums text-foreground">
                    <strong className="font-semibold">{numero.format(faixa.quantidade)}</strong>
                    <span className="text-muted-foreground"> · {Math.round(fatia)}%</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </CartaoGrafico>
      )}
    </div>
  );
};
